/**
 * BibleReadingPlanPage — wraps ReadingPlanViewer in a route, shows the
 * tenant context banner when the user came from a school/church/event.
 *
 * Route: /bible/plans/:planId/read?from=school&scopeId=<id>
 *
 * The path uses the public plan_id; we look up (or auto-start) the user's
 * matching user_plan and hand it to ReadingPlanViewer.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, GraduationCap, Church, CalendarDays, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ReadingPlanViewer from '@/components/bible/ReadingPlanViewer';

const ICON = { school: GraduationCap, church: Church, event: CalendarDays };
const LABEL = { school: 'school', church: 'church', event: 'event' };

export default function BibleReadingPlanPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { planId } = useParams();
  const [params] = useSearchParams();
  const from = params.get('from');
  const scopeId = params.get('scopeId');
  const [autoStarted, setAutoStarted] = useState(false);

  const { data: branding } = useQuery({
    queryKey: ['branding', from, scopeId],
    queryFn: () => api.get(`/api/branding/${from}/${scopeId}`).then((r) => r.data).catch(() => null),
    enabled: !!(from && scopeId && ['school', 'church', 'event'].includes(from)),
  });

  const { data: myPlans = [], isLoading } = useQuery({
    queryKey: ['my-plans'],
    queryFn: () => api.get('/bible/my-plans').then((r) => r.data),
  });
  const userPlan = (myPlans || []).find((p) => p.plan_id === planId);

  const startMutation = useMutation({
    mutationFn: () => api.post(`/bible/plans/${planId}/start`, { reading_plan_id: planId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['my-plans'] }); },
    onError: (e) => toast.error(e.response?.data?.detail || 'Failed to start'),
  });

  useEffect(() => {
    // Auto-start if the reader landed here without a subscription yet
    if (!isLoading && !userPlan && !autoStarted && !startMutation.isPending) {
      setAutoStarted(true);
      startMutation.mutate();
    }
  }, [isLoading, userPlan, autoStarted, startMutation]);

  const Icon = (from && ICON[from]) || GraduationCap;

  const goBack = () => {
    if (from === 'school' && scopeId) navigate(`/admin/schools/${scopeId}`);
    else if (from === 'event' && scopeId) navigate(`/admin/event/${scopeId}`);
    else if (from === 'church') navigate('/admin/church-hierarchy');
    else navigate('/bible');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Tenant context banner */}
      {from && (
        <div className="border-b bg-gradient-to-r from-indigo-50 to-violet-50 sticky top-0 z-10" data-testid="bible-tenant-banner">
          <div className="max-w-5xl mx-auto px-4 py-2 flex items-center gap-3 text-sm">
            <Button variant="ghost" size="icon" onClick={goBack} className="h-8 w-8" data-testid="bible-banner-back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="" className="h-7 w-7 rounded object-cover" />
            ) : (
              <div className="h-7 w-7 rounded bg-indigo-100 flex items-center justify-center">
                <Icon className="h-4 w-4 text-indigo-600" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">Reading with {branding?.name || `your ${LABEL[from]}`}</div>
              <div className="text-xs text-slate-500">Your progress is shared with your {LABEL[from]} admin.</div>
            </div>
            <Badge variant="outline" className="text-[10px] uppercase">{LABEL[from]}</Badge>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto p-4">
        {isLoading || !userPlan ? (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            {startMutation.isPending ? 'Starting your plan…' : 'Loading…'}
          </div>
        ) : (
          <ReadingPlanViewer userPlanId={userPlan.id} onBack={goBack} />
        )}
      </div>
    </div>
  );
}
